<template>
  <Transition name="navigation-loading-fade">
    <div
      v-if="isAppLoading"
      class="app-layout__loading-overlay"
    >
      <CadyLoading :message="appLoadingMessage" />
    </div>
  </Transition>

  <header class="container">
    <CadyHeader
      :nav-items="navItems"
      :show-cart="false"
      :show-sign-in="false"
    >
      <template #before-nav>
        <!-- Always shown as the "active" nav item — this whole site is the
             booking flow, so Book Session is always the current section. -->
        <div
          v-if="showBookSessionButton"
          class="cady-header__nav-item cady-header__nav-item--active"
        >
          <button
            type="button"
            class="nav-link header-font cady-header__nav-link app-header__book-session"
            @click="onBookSessionClick"
          >
            {{ contentStore.nav.bookSessionLabel }}
          </button>
        </div>
      </template>
    </CadyHeader>
  </header>
  <main role="main">
    <RouterView :key="contentStore.currentLocale" />
  </main>
  <footer class="app-footer">
    <CadyFooter />
  </footer>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useContentStore } from '@/stores/content'
import { useMainStore } from '@/stores/main'
import { mapState, mapStores } from 'pinia'

import { routeName } from '@/router/routeName'

import type { HeaderNavItem } from '@VelocitySquared/cady-design-system'
import { CadyFooter, CadyHeader, CadyLoading } from '@VelocitySquared/cady-design-system'

import { buildNavItems } from '@/content/shared/nav'

export default defineComponent({
  name: 'AppLayout',
  components: {
    CadyFooter,
    CadyHeader,
    CadyLoading
  },
  computed: {
    ...mapStores(useContentStore, useMainStore),
    ...mapState(useMainStore, ['isAppLoading', 'appLoadingMessage', 'bookingIsOpen']),
    isOnLandingPage(): boolean {
      return this.$route.name === routeName.LANDING_PAGE
    },
    showBookSessionButton(): boolean {
      return this.isOnLandingPage && this.bookingIsOpen
    },
    navItems(): HeaderNavItem[] {
      return buildNavItems(this.contentStore.nav)
    }
  },
  methods: {
    onBookSessionClick() {
      this.mainStore.showFindSchoolModal = true
    }
  },
  watch: {
    'contentStore.currentLocale'() {
      this.contentStore.resetStore()
    }
  }
})
</script>

<style lang="scss" scoped>
// Resets default <button> chrome so it matches CadyHeader's own <a>-based
// nav-link styling exactly (font/color/hover/spacing come from the shared
// nav-link/nav-item classes applied alongside this one).
.app-header__book-session {
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;

  @include media-breakpoint-up(lg) {
    width: auto;
  }
}

main {
  min-height: 40vh;
}

.app-footer {
  background-color: $color-black;
}

.app-layout__loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  // Fully opaque so chrome (header, footer, sticky footer) doesn't bleed through.
  // Avoids needing to v-show the chrome separately, which interfered with the
  // Lottie animation initialization.
  background-color: $color-white;
}

.navigation-loading-fade-enter-active {
  transition: opacity 0.15s ease;
}

.navigation-loading-fade-leave-active {
  transition: opacity 0.25s ease;
}

.navigation-loading-fade-enter-from,
.navigation-loading-fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
#app-sticky-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0.75rem;
  background-color: $color-white;
  box-shadow: 0 -5px 12px -5px rgba(0, 0, 0, 0.18);
  z-index: 1040;

  @include media-breakpoint-up(md) {
    gap: 1rem;
    padding: 2rem;
    box-shadow: 1px 0 12px 0 rgba(0, 0, 0, 0.15);
  }

  &:empty {
    display: none;
  }
}
</style>
